import { assertNotEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useMemo } from 'react'

import { StackableTable } from '../../components/stackableTable/StackableTable'
import type {
  StackableType,
  ValidStackableRatios,
} from '../../components/stackableTable/types'
import { useIsInlineEditingAvailable } from '../../hooks/useIsInlineEditingAvailable'
import type {
  EnumDefinition,
  EnumLiteral,
} from '../../uiModels/enumLiterals/types'
import { EnumChildRowCell } from './EnumChildRowCell'
import { EnumRowCell } from './EnumRowCell'
import {
  AddLiteralPlaceholder,
  type EnumElementSelection,
  isAddLiteralPlaceholder,
} from './types'

const ENUM_HEADER_COLUMN_RATIOS = [1, 2] as ValidStackableRatios[]

type Props = {
  onViewEnumDetails: () => void
  onViewLiteralDetails: (literal: string) => void
  value: EnumDefinition
  selection: Optional<EnumElementSelection>
}

export const EnumTable = ({
  onViewEnumDetails,
  onViewLiteralDetails,
  value,
  selection,
}: Props) => {
  const translate = useTranslate()
  const isEditable = useIsInlineEditingAvailable()

  const parent: StackableType<EnumDefinition> = useMemo(
    () => ({
      value,
      key: () => value.name ?? '',
      columns: 3,
      ratios: ENUM_HEADER_COLUMN_RATIOS,
      errors: value.errors,
      isActive: selection?.variant === 'definition',
      shouldShowOnOverflow: true,
    }),
    [selection?.variant, value],
  )

  const stackableLiterals = useMemo(() => {
    const allLiterals: StackableType<
      EnumLiteral | typeof AddLiteralPlaceholder
    >[] = [...value.literals]
      .sort((literalA, literalB) => {
        const literalNameA =
          (literalA.type === 'modeled'
            ? literalA.literalName
            : literalA.internalOperation.entity?.name) ?? ''
        const literalNameB =
          (literalB.type === 'modeled'
            ? literalB.literalName
            : literalB.internalOperation.entity?.name) ?? ''

        return literalNameA.localeCompare(literalNameB, undefined, {
          sensitivity: 'base',
        })
      })
      .map(
        (literal) =>
          ({
            value: literal,
            key: () =>
              literal.type === 'modeled'
                ? `modeled-${literal.literalName}`
                : `configured-${literal.internalOperation.entity?.name ?? ''}`,
            isActive:
              selection?.variant === 'literal' &&
              selection.value ===
                (literal.type === 'modeled'
                  ? literal.literalName
                  : literal.internalOperation.entity?.name),
            columns: 3,
            ratios: ENUM_HEADER_COLUMN_RATIOS,
            errors: literal.type === 'configured' ? literal.errors : [],
            shouldShowOnOverflow: true,
          }) satisfies StackableType<EnumLiteral>,
      )

    if (isEditable) {
      allLiterals.push({
        value: AddLiteralPlaceholder,
        key: () => AddLiteralPlaceholder.toString(),
        isActive: false,
        columns: 1,
        ratios: [],
        errors: [],
        shouldShowOnOverflow: false,
        isEmpty: true,
      } satisfies StackableType<typeof AddLiteralPlaceholder>)
    }

    return allLiterals
  }, [isEditable, selection, value.literals])

  const handleChildClicked = (
    childElement: StackableType<EnumLiteral | typeof AddLiteralPlaceholder>,
  ) => {
    if (isAddLiteralPlaceholder(childElement.value)) {
      return
    }

    const clickedLiteralName =
      childElement.value.type === 'modeled'
        ? childElement.value.literalName
        : childElement.value.internalOperation.entity?.name

    assertNotEmpty(clickedLiteralName)

    onViewLiteralDetails(clickedLiteralName)
  }

  return (
    <StackableTable
      indentationStep={0}
      parentCell={EnumRowCell}
      parent={parent}
      rowClassName="min-h-16"
      emptyRowColumnRatios={ENUM_HEADER_COLUMN_RATIOS}
      headers={[
        translate('enumViewer.header.name'),
        translate('enumViewer.header.type'),
        translate('enumViewer.header.description'),
      ]}
      onParentClicked={onViewEnumDetails}
    >
      <StackableTable
        indentationStep={1}
        childCell={EnumChildRowCell}
        data={stackableLiterals}
        rowClassName="min-h-16"
        emptyRowColumnRatios={ENUM_HEADER_COLUMN_RATIOS}
        onChildClicked={handleChildClicked}
      />
    </StackableTable>
  )
}
